import {GigyaUtils} from '../utils/GigyaUtils';
import cookieReader from 'cookies-js';

/**
 * This class represents the status of gigya sdk and emits 
 * events on login and logout.
 * 
 * @class GigyaModel
 * @extends Backbone.Model
 * 
 * @property isLoaded $.Deferred()
 * @event gigya:loaded
 * @event gigya:login
 * @event gigya:logout
 */
export class GigyaModel extends Backbone.Model {

    initialize () {

        if(_.isUndefined(window.gigya)){
            // gigya is not loaded, poll every 30 ms.
            let timeout = 30;

            this._poll = setInterval(()=>{
                if(!_.isUndefined(window.gigya)){
                    this.onGigyaLoaded();
                }
            }, timeout);
        } else {
            // gigya is loaded
            this.onGigyaLoaded();
        }
    }

    defaults () {

        return {
            isLoaded: $.Deferred(),
        };
    }

    onGigyaLoaded () {

        if(_.isUndefined(this._poll)){
            clearInterval(this._poll);
        }
        this.get('isLoaded').resolve();
        // gigya.accounts.addEventHandlers({
        //     onLogin :   this.onGigyaLogin,
        //     onLogout:   this.onGigyaLogout
        // });
        this.trigger('gigya:loaded');
    }

    onGigyaLogout (data) {

        this.trigger('gigya:logout', data);
    }

    onGigyaLogin (data) {

        this.trigger('gigya:login', data);
    }

    _proxy (method,  parameters) {

        let deferred = $.Deferred();

        if(this.get('isLoaded').state() === 'resolved'){
            // extend parameters
            parameters = _.extend({}, parameters);
            // overwrite parameters.callback to resolve/reject deferred
            delete parameters.callback;
            parameters.callback = (response) => {
                if(GigyaUtils.getGigyaErrorMessage(response)){
                    // login failed
                    deferred.reject(response);
                } else {
                    // login successful
                    deferred.resolve(response);
                }
            };
            method(parameters);
        }

        return deferred;
    }

    /**
     * see gigya.accounts.login
     * 
     * @param {any} parameters see gigya documentation, do not use callback
     * @returns $.Deferred()
     * 
     * @memberOf GigyaModel
     */
    login(parameters){

        if (IS_REMOTE_DEPLOYMENT) {

            let deferred = $.Deferred();

            let data = {
                include: 'emails,profile,data',
                loginId: parameters.loginId,
                password: parameters.password,
                loginMode: parameters.loginMode,
                apiKey: GIGYA_API_KEY
            };

            $.ajax({
                url: `${GIGYA_API_HOST}/accounts.login`,
                type: 'GET',
                ignoreSecurityOptions: true,
                dataType: 'json',
                data: data
            }).done((response) => {

                let options = {};

                if (ENV !== 'local') {
                    options = {
                        domain: TOKEN_DOMAIN
                    };
                }

                cookieReader.set(GIGYA_LOGIN_TOKEN_NAME, response.sessionInfo.cookieValue, options);

                deferred.resolve(response);
            })
            .fail((response) => {
                deferred.reject(response);
            });

            return deferred;
        } else {
            return this._proxy(gigya.accounts.login, parameters);
        }
    }

    /**
     * see gigya.accounts.logout
     * 
     * @param {any} parameters see gigya documentation, do not use callback
     * @returns $.Deferred()
     * 
     * @memberOf GigyaModel
     */
    logout(parameters){

        if (IS_REMOTE_DEPLOYMENT) {

            let deferred = $.Deferred();

            let data = {
                UID: parameters.UID,
                apiKey: GIGYA_API_KEY,
                login_token: cookieReader.get(GIGYA_LOGIN_TOKEN_NAME)
            };

            $.ajax({
                url: `${GIGYA_API_HOST}/accounts.logout`,
                type: 'GET',
                ignoreSecurityOptions: true,
                dataType: 'json',
                data: data
            }).done((response) => {

                let options = {};

                if (ENV !== 'local') {
                    options = {
                        domain: TOKEN_DOMAIN
                    };
                }

                // expire second-level domain
                cookieReader.expire(GIGYA_LOGIN_TOKEN_NAME, options);

                // expire third-level domain (legacy)
                cookieReader.expire(GIGYA_LOGIN_TOKEN_NAME);

                deferred.resolve(response);
            })
            .fail((response) => {
                deferred.reject(response);
            });

            return deferred;
        } else {
            return this._proxy(gigya.accounts.logout, parameters);
        }
    }
    
    /**
     * see gigya.accounts.getAccountInfo
     * 
     * @param {any} parameters see gigya documentation, do not use callback
     * @returns $.Deferred()
     * 
     * @memberOf GigyaModel
     */
    getAccountInfo (parameters) {

        if (IS_REMOTE_DEPLOYMENT) {

            let deferred = $.Deferred();

            let data = {
                UID: parameters.UID,
                apiKey: GIGYA_API_KEY,
                login_token: cookieReader.get(GIGYA_LOGIN_TOKEN_NAME),
                include: parameters.include,
                extraProfileFields: parameters.extraProfileFields
            };

            $.ajax({
                url: `${GIGYA_API_HOST}/accounts.getAccountInfo`,
                type: 'GET',
                ignoreSecurityOptions: true,
                dataType: 'json',
                data: data
            }).done((response) => {
                deferred.resolve(response);
            })
            .fail((response) => {
                deferred.reject(response);
            });

            return deferred;
        } else {
            return this._proxy(gigya.accounts.getAccountInfo, parameters);
        }
    }

    /**
     * see gigya.accounts.resetPassword
     * 
     * @param {any} parameters see gigya documentation, do not use callback
     * @returns $.Deferred()
     * 
     * @memberOf GigyaModel
     */
    resetPassword (parameters) {

        return this._proxy(gigya.accounts.resetPassword, parameters);
    }

    /**
     * see gigya.accounts.setAccountInfo
     * 
     * @param {any} parameters see gigya documentation, do not use callback
     * @returns $.Deferred()
     * 
     * @memberOf GigyaModel
     */
    setAccountInfo (parameters) {

        if (IS_REMOTE_DEPLOYMENT) {

            let deferred = $.Deferred();

            let data = {
                UID: parameters.UID,
                profile: parameters.profile,
                data: parameters.data,
                apiKey: GIGYA_API_KEY,
                login_token: cookieReader.get(GIGYA_LOGIN_TOKEN_NAME),
            };

            $.ajax({
                url: `${GIGYA_API_HOST}/accounts.setAccountInfo`,
                type: 'GET',
                ignoreSecurityOptions: true,
                dataType: 'json',
                data: data
            }).done((response) => {
                deferred.resolve(response);
            })
            .fail((response) => {
                deferred.reject(response);
            });

            return deferred;
        } else {
            return this._proxy(gigya.accounts.setAccountInfo, parameters);
        }
    }
}


// WEBPACK FOOTER //
// ./shared/model/GigyaModel.js